import { addDecimals, applyDiscount, calculateTax, divideDecimals, subtractDecimals } from '@proposify/pricing-engine';
import type { TableRowType } from '../../../../grid/reduxStore/table.types';
import { TotalRowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { PERCENT_REFERENCE, PERCENT_SIGN } from '../defaultData.tsx';

const FORMATTER_KEY = 'price';

export type FooterCombinedTotals = {
  /** Decimal-string display value per TAX/DISCOUNT/TOTAL row id. */
  rowValues: Record<string, string>;
  /** base − discounts + taxes, decimal string. */
  combinedTotal: string;
};

type RowAmount = {
  value: string;
  isPercentage: boolean;
};

// Mirrors getRowNumericValue in updateTotalRowsWithSubtotalCalculation, but keeps
// the amount a decimal string instead of coercing to float.
const getRowAmount = (row: TableRowType): RowAmount => {
  const raw = String(row[FORMATTER_KEY] ?? '');
  const value = raw !== '' && Number.isFinite(Number(raw)) ? raw : '0';
  const valueFormatter = row?.cellConfig?.[FORMATTER_KEY]?.valueFormatter || '';

  return { value, isPercentage: valueFormatter === PERCENT_SIGN || valueFormatter === PERCENT_REFERENCE };
};

// Footer rows store percentages as 0–100; engine discount/tax values are fractions.
const toFraction = (percentValue: string): string => divideDecimals(percentValue, '100');

/**
 * Combined footer math for mixed-frequency tables (SP-3794). Applies footer
 * Tax/Discount rows to the engine's combined base (sum of every frequency
 * bucket) using engine primitives only, so values stay decimal strings until
 * display. Ordering matches the legacy footer: discounts come off the base,
 * every tax row is computed on the post-discount base.
 *
 * Deliberately NOT routed through computeTablePricing inputs: table-scope
 * discounts there are distributed into frequencyTotals, which would silently
 * turn the SP-3778 per-frequency subtotal rows post-discount.
 */
export function getFooterCombinedTotals(combinedBase: string, totalRows: TableRowType[]): FooterCombinedTotals {
  const rowValues: Record<string, string> = {};

  // RISK: engine applyDiscount clamps a fixed discount at the base amount; the legacy
  // float path lets it exceed the base (negative Total). Combined mode floors at zero.
  const totalDiscount = totalRows.reduce((sum, row) => {
    if (row.rowType !== TotalRowTypesIdentifier.DISCOUNT) return sum;
    const { value, isPercentage } = getRowAmount(row);
    const { discountAmount } = applyDiscount(combinedBase, {
      type: isPercentage ? 'percentage' : 'fixed',
      value: isPercentage ? toFraction(value) : value,
      scope: 'total',
      stackable: false,
    });
    rowValues[String(row.id)] = discountAmount;
    return addDecimals(sum, discountAmount);
  }, '0');

  const taxableBase = subtractDecimals(combinedBase, totalDiscount);

  const totalTax = totalRows.reduce((sum, row) => {
    if (row.rowType !== TotalRowTypesIdentifier.TAX) return sum;
    const { value, isPercentage } = getRowAmount(row);
    // RISK: the engine has no fixed-amount tax input (TaxConfig is rate-only), so a
    // fixed tax row is composed as a plain decimal addend rather than an engine call.
    const taxAmount = isPercentage
      ? calculateTax(taxableBase, { region: 'table-footer', rate: toFraction(value), inclusive: false, roundingMode: 'nearest' }).taxAmount
      : value;
    rowValues[String(row.id)] = taxAmount;
    return addDecimals(sum, taxAmount);
  }, '0');

  const combinedTotal = addDecimals(taxableBase, totalTax);

  const totalRow = totalRows.find((row) => row.rowType === TotalRowTypesIdentifier.TOTAL);
  if (totalRow) {
    rowValues[String(totalRow.id)] = combinedTotal;
  }

  return { rowValues, combinedTotal };
}
